
import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Youtube, Languages } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useLiveTranscription } from '@/hooks/useLiveTranscription';
import { useIsMobile } from '@/hooks/use-mobile';
import { getVideoTranscription, extractVideoId } from '@/services/youtube-transcription';
import { useToast } from '@/hooks/use-toast';
import { fixNativeIosViewportAfterOverlay } from '@/utils/iosViewport';

interface SpeechToTextButtonProps {
  onTranscriptionComplete: (text: string) => void;
}

const SpeechToTextButton: React.FC<SpeechToTextButtonProps> = ({ onTranscriptionComplete }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // On iOS WKWebView, reliable interaction depends on treating iOS as a special case
  // even if Capacitor's native checks behave unexpectedly in some environments.
  const isIos = Capacitor.getPlatform() === 'ios';
  const isNativeIos = Capacitor.isNativePlatform?.() && isIos;
  
  const { isListening, toggleListening, isSupported } = useSpeechRecognition({
    onTranscriptionComplete,
    language
  });

  const { isRecording, startRecording, stopRecording, accumulatedText } = useLiveTranscription({
    onTranscriptionUpdate: onTranscriptionComplete,
    language
  });

  const lastTapAtRef = useRef(0);
  const hasPrimedIosMicRef = useRef(false);
  const isPrimingIosMicRef = useRef(false);
  const prevRecordingRef = useRef(false);

  const scheduleIosViewportFix = () => {
    if (!isIos) return;
    // WKWebView sometimes applies zoom/scroll adjustments after permission prompts.
    // Run more than once to catch late layout changes.
    fixNativeIosViewportAfterOverlay();
    requestAnimationFrame(() => fixNativeIosViewportAfterOverlay());
    window.setTimeout(() => fixNativeIosViewportAfterOverlay(), 250);
  };

  // iOS WKWebView can leave the page horizontally panned after a portaled overlay +
  // permission prompts. When recording stops, normalize viewport position.
  useEffect(() => {
    if (!isNativeIos) return;
    const prev = prevRecordingRef.current;
    prevRecordingRef.current = isRecording;
    // Normalize viewport when recording starts or stops (iOS can zoom after mic permissions).
    if ((prev && !isRecording) || (!prev && isRecording)) scheduleIosViewportFix();
  }, [isNativeIos, isRecording]);

  const primeNativeIosMicrophone = (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
    // iOS WKWebView is strict: the *permission prompt* must be initiated directly
    // from a user gesture (touch/pointer). We prime the mic on down-start so that
    // the subsequent async Gladia flow can run reliably.
    if (!isNativeIos) return;
    if (hasPrimedIosMicRef.current || isPrimingIosMicRef.current) return;
    if (!navigator?.mediaDevices?.getUserMedia) return;

    isPrimingIosMicRef.current = true;

    // Keep the gesture context as intact as possible: no awaits here.
    // (Don’t preventDefault unless absolutely necessary.)
    try {
      const p = navigator.mediaDevices.getUserMedia({ audio: true });
      void p
        .then((stream) => {
          stream.getTracks().forEach((t) => t.stop());
          hasPrimedIosMicRef.current = true;
          scheduleIosViewportFix();
        })
        .catch((err: any) => {
          console.error('iOS mic prime getUserMedia failed:', err);
          toast({
            title: 'Microphone Permission',
            description:
              err?.name === 'NotAllowedError'
                ? 'Microphone permission was denied. Please enable it in iOS Settings for this app.'
                : 'Could not access the microphone. Please check iOS permissions and try again.',
            variant: 'destructive',
          });
          scheduleIosViewportFix();
        })
        .finally(() => {
          isPrimingIosMicRef.current = false;
          scheduleIosViewportFix();
        });
    } catch (err) {
      console.error('iOS mic prime threw:', err);
      isPrimingIosMicRef.current = false;
      scheduleIosViewportFix();
    }
  };

  const handleLiveRecordingToggle = async () => {
    console.log('SpeechToTextButton: live recording toggle', {
      isNativeIos,
      isRecording,
      isPopoverOpen,
    });

    if (isRecording) return stopRecording();
    return startRecording();
  };

  const handleLiveRecordingTap = (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
    // On iOS WKWebView, multiple events (touch + click) can fire; de-dupe.
    const now = Date.now();
    if (now - lastTapAtRef.current < 600) return;
    lastTapAtRef.current = now;

    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Ensure iOS native mic permission is triggered from the gesture.
    primeNativeIosMicrophone(e);

    // If the native permission prompt causes a zoom/viewport shift, immediately normalize.
    scheduleIosViewportFix();

    // Don't await inside the event handler; keep the iOS "user gesture" context as intact as possible.
    void handleLiveRecordingToggle();
  };

  const handleVideoTranscription = async () => {
    if (!videoUrl.trim()) {
      toast({
        title: "Missing URL",
        description: "Please enter a video URL",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProcessingMessage("Validating video URL...");
    
    try {
      console.log('Starting video transcription process...');
      
      const videoInfo = extractVideoId(videoUrl);
      if (!videoInfo) {
        toast({
          title: "Invalid Video URL",
          description: "Please enter a valid YouTube, TikTok, Instagram, or X (Twitter) URL",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }
      
      console.log(`${videoInfo.platform} video ID extracted:`, videoInfo.videoId);
      setProcessingMessage("Fetching video details...");
      
      const result = await getVideoTranscription(videoUrl);
      
      console.log('Transcription result:', {
        success: result.success,
        title: result.title,
        transcriptionLength: result.transcription?.length || 0,
        error: result.error
      });
      
      if (result.success && result.transcription) {
        setVideoTitle(result.title);
        const formattedTranscription = `Transcription of "${result.title}":\n\n${result.transcription}`;
        onTranscriptionComplete(formattedTranscription);
        setVideoUrl('');
        setIsPopoverOpen(false);
        
        toast({
          title: "Transcription complete",
          description: `Successfully processed "${result.title}"`,
        });
      } else {
        toast({
          title: "Transcription failed",
          description: result.error || "Could not process this video. Please try another video.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Video transcription error:', error);
      toast({
        title: "Transcription error",
        description: "Failed to process the video. Please check your internet connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const languageOptions = [
    { value: "en-US", label: "English (US)" },
    { value: "en-GB", label: "English (UK)" },
    { value: "es-ES", label: "Spanish" },
    { value: "fr-FR", label: "French" },
    { value: "de-DE", label: "German" },
    { value: "it-IT", label: "Italian" },
    { value: "ja-JP", label: "Japanese" },
    { value: "ko-KR", label: "Korean" },
    { value: "pt-BR", label: "Portuguese" },
    { value: "ru-RU", label: "Russian" },
    { value: "zh-CN", label: "Chinese (Simplified)" }
  ];

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        if (!open && isNativeIos) {
          fixNativeIosViewportAfterOverlay();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={isListening ? 'bg-red-100 hover:bg-red-200' : ''}
          title={isListening ? 'Stop recording' : 'Speech to text'}
          onClick={isListening ? toggleListening : undefined}
        >
          {isListening ? (
            <MicOff className="h-4 w-4 text-red-500" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`${isMobile ? 'w-[92vw] max-w-[92vw] p-3' : 'w-80'} max-h-[85vh] overflow-y-auto overflow-x-hidden`}>
        <div className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
          <h4 className={`font-medium text-center ${isMobile ? 'text-base' : ''}`}>Speech to Text</h4>

          <Tabs defaultValue="livemeeting" className="w-full">
            <TabsList className={`grid grid-cols-2 w-full ${isMobile ? 'h-auto mb-3' : 'mb-4'}`}>
              <TabsTrigger value="livemeeting" className={`${isMobile ? 'text-sm px-2 py-2' : ''}`}>Live</TabsTrigger>
              <TabsTrigger value="youtube" className={`${isMobile ? 'text-sm px-2 py-2' : ''}`}>Video</TabsTrigger>
            </TabsList>
            
            
            <TabsContent value="livemeeting" className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
              <div className="space-y-2">
                <Label htmlFor="meeting-language" className={`${isMobile ? 'text-sm' : ''}`}>Language</Label>
                <Select 
                  value={language} 
                  onValueChange={setLanguage}
                  disabled={isRecording}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a language" />
                  </SelectTrigger>
                  <SelectContent className={isMobile ? "max-h-[40vh]" : ""}>
                    {languageOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                type="button"
                className="w-full touch-manipulation"
                variant={isRecording ? "destructive" : "default"}
                // iOS WKWebView can drop bubble-phase click events inside portaled popovers.
                // Use onTouchEnd only (not multiple handlers) to prevent double-firing.
                onClick={!isIos ? (e) => void handleLiveRecordingTap(e) : undefined}
                // Some iOS builds prefer Pointer Events, others Touch Events.
                // We attach both and rely on the built-in de-dupe in handleLiveRecordingTap.
                onPointerDown={isNativeIos ? (e) => void primeNativeIosMicrophone(e) : undefined}
                onTouchStart={isNativeIos ? (e) => void primeNativeIosMicrophone(e) : undefined}
                onPointerUp={isIos ? (e) => void handleLiveRecordingTap(e) : undefined}
                onTouchEnd={isIos ? (e) => void handleLiveRecordingTap(e) : undefined}
              >

                {isRecording ? (
                  <>
                    <MicOff className="mr-2 h-4 w-4" />
                    Stop Recording
                  </>
                ) : (
                  <>
                    <Mic className="mr-2 h-4 w-4" />
                    Start Recording
                  </>
                )}
              </Button>
              
              <div className={`text-xs text-muted-foreground ${isMobile ? 'text-[11px]' : ''}`}>
                <p>Suitable for dictation to text or in-person meetings. Includes timestamps (0m 27s) that you can remove with a prompt.</p>
              </div>
            </TabsContent>
            
            <TabsContent value="youtube" className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
              <div className="space-y-2">
                <Label htmlFor="video-url" className={`${isMobile ? 'text-sm' : ''}`}>Video URL</Label>
                <div className="flex space-x-2">
                  <Input 
                    id="video-url" 
                    placeholder="Paste YouTube, TikTok, Instagram, or X URL here..." 
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    disabled={isProcessing}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isProcessing && videoUrl.trim()) {
                        handleVideoTranscription();
                      }
                    }}
                  />
                </div>
              </div>
              <Button 
                className="w-full" 
                onClick={handleVideoTranscription} 
                disabled={isProcessing || !videoUrl.trim()}
              >
                <Youtube className="mr-2 h-4 w-4" />
                {isProcessing ? 'Processing...' : 'Transcribe Video'}
              </Button>
              
              <div className={`text-xs text-muted-foreground ${isMobile ? 'text-[11px]' : ''}`}>
                <p>Supports YouTube, TikTok and Instagram videos.</p>
              </div>
              
              {isProcessing && (
                <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>{processingMessage || "Processing video..."}</span>
                  </div>
                  <div className="mt-2 text-xs text-blue-500">
                    This may take a few moments depending on video length...
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className={`text-xs text-muted-foreground ${isMobile ? 'text-[11px]' : ''}`}>
            <p>For best results, speak clearly and ensure you are in a quiet environment.</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SpeechToTextButton;
